import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import { useRoute } from "vue-router";
import { NO_RESULT_MESSAGE, API_ERROR_MESSAGE } from "@/strings.js";
import { getEnv } from "../utils/env";


// see https://vitejs.dev/guide/env-and-mode.html
// for setting production env
// To make working with App Engine Previews easier, use domain from globalThis
// since front and backend are served from the same domain
// on cloud.gov / pages, VITE_API_DOMAIN will need to point to the cloud.gov instance
// const API_DOMAIN = import.meta.env.VITE_API_DOMAIN;
// const API_DOMAIN = "https://889tool-backend.dev.smartpay.mcaas.fcs.gsa.gov";
const API_DOMAIN = getEnv("API_DOMAIN") || getEnv("VITE_API_DOMAIN")

const DEFAULT_PAGE_SIZE = 10;


export const useSearchStore = defineStore("search", () => {
  const data = ref([]);
  const loading = ref(false);
  const error = ref("");
  const APIMessage = ref("");
  const search_text = ref("");
  const currentPageIndex = ref(0);
  const totalRecords = ref(0);
  const isFirstLoad = ref(true);

  const numberOfPages = computed(() =>
    Math.ceil(totalRecords.value / DEFAULT_PAGE_SIZE),
  );
  const showResults = computed(() => data.value.length > 0);

  const route = useRoute();

  watch(
    () => route.params,
    (params) => {
      const hasRouteSearchParams = "term" in params || "page" in params;
      if (isFirstLoad.value === false && hasRouteSearchParams) {
        search_text.value = params.term;
        currentPageIndex.value = params.page ? Number.parseInt(params.page) : 0;
        fetchResults();
      }
    },
  );

  function resetState() {
    search_text.value = "";
    currentPageIndex.value = 0;
  }

  function initState() {
    search_text.value = route.params.term;
    currentPageIndex.value = Number.parseInt(route.params.page) || 0;
    isFirstLoad.value = false;
    fetchResults();
  }

  function fetchResults() {
    console.log("Fetching results with API_DOMAIN:", API_DOMAIN);
    loading.value = true;
    error.value = "";
    APIMessage.value = "";
    
    // Validate API_DOMAIN before constructing URL
    const hasValidApiDomain = Boolean(API_DOMAIN) && typeof API_DOMAIN === "string";
    if (hasValidApiDomain === false) {
      loading.value = false;
      error.value = "API configuration error. Please check API_DOMAIN setting.";
      console.error("API_DOMAIN is not properly configured:", API_DOMAIN);
      return;
    }
    
    const url_params = {
      samToolsSearch: search_text.value,
      page: currentPageIndex.value || 0,
    };
    
    let url;
    try {
      url = new URL(`${API_DOMAIN}/api/entity-information/v3/entities`);
      url.search = new URLSearchParams(url_params).toString();
    } catch (urlError) {
      loading.value = false;
      error.value = "API configuration error. Invalid API domain.";
      console.error("Failed to construct URL:", urlError, "API_DOMAIN:", API_DOMAIN);
      return;
    }

    
    fetch(url)
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
        throw new Error(`Request failed with status ${res.status}`);
      })
      .then((json) => {
        data.value = json.entityData;
        totalRecords.value = json.totalRecords;
        if (totalRecords.value == 0) {
          APIMessage.value = NO_RESULT_MESSAGE;
        }
        loading.value = false;
      })
      .catch(() => {
        data.value = [];
        totalRecords.value = 0;
        loading.value = false;
        error.value = API_ERROR_MESSAGE;
      });
  }

  return {
    data,
    search_text,
    numberOfPages,
    APIMessage,
    currentPageIndex,
    totalRecords,
    showResults,
    loading,
    error,
    initState,
    resetState,
  };
});